import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogProps,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import { ReactNode, useState } from "react";
import { useLoading } from "../../hooks/use-loading";
import { getErrorSnackbar, getSuccessSnackbar } from "../../utils/utils";
import Flex from "../Flex";
import LoadingOverlay from "../LoadingOverlay";
import Text from "../Text";
import CustomTitle from "./CustomTitle";
import { useSnackbar } from "../../hooks/useSnackbar";
import { DisclosureHost } from "../../hooks/use-disclosure";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

type DeleteModalProps<T> = {
  content: ReactNode;
  open?: boolean;
  onClose?: () => void;
  disclosureHost?: DisclosureHost;
  onConfirmation: (id?: T) => Promise<any>;
  id?: T;
  disabledConfirmation?: boolean;
  h?: string;
  w?: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  title?: string;
  iconColor?: string;
  buttonColor?: string;
  input?: JSX.Element;
  variant?: "delete" | "warning";
  disableOnSaveSnackbar?: boolean;
} & Omit<DialogProps, "content">;

/**
 *
 * @param content - The content of the modal
 * @param open - The open state of the modal
 * @param onClose - The on close function
 * @param disclosureHost - The disclosure host
 * @param onConfirmation - The on confirmation function
 * @param id - The id of the item
 * @param w - The width of the modal
 * @param title - The title of the modal
 * @param cancelButtonText - The cancel button text
 * @param confirmButtonText - The confirm button text
 * @param iconColor - The icon color
 * @param input - The input element
 * @param disableOnSaveSnackbar - The disable on save snackbar
 * @param others - The other props
 * @returns The delete modal component
 *
 * @author [Rajkumar Rathod]
 */
const DeleteModal = <T,>({
  content,
  /**
   * @deprecated since v2.3.0 — use `disclosureHost` instead.
   */
  open = false,
  /**
   * @deprecated since v2.3.0 — use `disclosureHost` instead.
   */
  onClose,
  disclosureHost,
  onConfirmation,
  id,
  w = "10vw",
  title,
  cancelButtonText,
  confirmButtonText,
  disabledConfirmation = false,
  iconColor,
  buttonColor = "primary",
  input,
  variant = "delete",
  disableOnSaveSnackbar = false,
  ...others
}: DeleteModalProps<T>) => {
  /**
   * handle loading inside this component
   * so that we can reduce snippet for loading state
   */
  const [isLoading, setIsLoading] = useLoading();
  const showSnackbar = useSnackbar();
  return (
    <Dialog
      open={open || (disclosureHost?.opened ?? false)}
      maxWidth={"md"}
      {...others}
    >
      {title && (
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
              }}
            >
              {title}
            </Box>
            <IconButton onClick={onClose || disclosureHost?.close}>
              <GridCloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}
      <Flex
        justifyContent={"center"}
        alignItems={"center"}
        flexDirection={"column"}
        width={w}
        px={2}
      >
        <LoadingOverlay isLoading={isLoading} />
        <Flex
          style={{
            padding: "10px",
            // border: `1px solid ${iconColor ?? "#d32f2f"}`,
            backgroundColor: "#FFF2F3",
            borderRadius: "50%",
          }}
          width={"fit-content"}
          mt={"12px"}
        >
          {input || variant === "warning" ? (
            <WarningAmberRoundedIcon
              fontSize="large"
              sx={{
                color: "error.main",
                marginBottom: "2px",
              }}
            />
          ) : (
            <DeleteOutlineIcon
              fontSize="large"
              sx={{
                color: iconColor ?? "error.main",
                marginBottom: "2px",
              }}
            />
          )}
        </Flex>
        <CustomTitle
          size={18}
          color={"black"}
          fontWeight={"medium"}
          marginTop={"12px"}
        >
          Are You Sure ?
        </CustomTitle>
        <Text my={"10px"} c={"#1A1A1A99"} mb={input ? "26px" : "0px"}>
          {content}
        </Text>
        {input}
      </Flex>
      <Flex justifyContent={"center"} alignItems={"end"} p={2} gap={"10px"}>
        <Button
          onClick={onClose || disclosureHost?.close}
          variant="outlined"
          fullWidth
          sx={{
            borderColor: `${buttonColor}.main`,
            color: `${buttonColor}.main`,
            backgroundColor: "white",
            "&:hover": {
              borderColor: `${buttonColor}.dark`,
              backgroundColor: "white",
            },
          }}
        >
          {cancelButtonText ?? "No"}
        </Button>
        <Button
          variant="contained"
          disabled={disabledConfirmation}
          onClick={() => {
            setIsLoading(true);
            onConfirmation?.(id)
              .then((res) => {
                if (!disableOnSaveSnackbar) {
                  showSnackbar(getSuccessSnackbar(res));
                }
                onClose?.() || disclosureHost?.close();
              })
              .catch((err) => showSnackbar(getErrorSnackbar(err)))
              .finally(() => setIsLoading(false));
          }} //returning !prev value
          fullWidth
          sx={{
            backgroundColor: `${buttonColor}.main`,
            color: "white",
            "&:hover": {
              backgroundColor: `${buttonColor}.dark`,
            },
          }}
        >
          {confirmButtonText ?? "Yes Sure"}
        </Button>
      </Flex>
    </Dialog>
  );
};

/**
 * Creating this hook to manage the DELETE modal
 *
 * while call the open state just need to pass id which you wanna delete
 * @assumption opening the delete modal that means need to delete some data
 * and possibly id is necessary to delete
 *
 * after using this hook:
 * - is no need to manage the extra state to handle the delete item id
 *
 * @param isOpened
 * @param onOpen
 * @param onClose
 *
 * @returns  {opened, open, close, deleteId}
 * @returns `deleteId` just need to pass to modal and you will get this while confirmation of delete
 *
 * @author rajkumar rathod
 */
export const useDeleteDisClosure = (
  isOpened?: boolean,
  onOpen?: () => void,
  onClose?: () => void
) => {
  const [opened, setOpened] = useState<boolean>(!!isOpened);
  const [deleteId, setDeleteId] = useState<string>();

  const open = (id?: number | string) => {
    setDeleteId(id?.toString());
    setOpened(true);
    onOpen?.();
  };

  const close = () => {
    setOpened(false);
    onClose?.();
  };

  return { opened, open, close, deleteId };
};

export default DeleteModal;
